import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { useActiveOrganizationId } from '@/features/auth/session';
import { useFirestoreDocument } from '@/hooks/useFirestoreDocument';
import { apiClient } from '@/lib/apiClient';
import { collections } from '@/lib/firebase';
import { setBaseBrandColors, setPreviewBrandColors } from './brandTheme';

export interface Organization {
  organizationId: string;
  name: string;
  logoObject: string | null;
  smallLogoObject: string | null;
  primaryColor: string;
  secondaryColor: string;
  hasSingleBrand: boolean;
}

interface OrganizationResponse {
  organization_id: string;
  name: string;
  logo_object: string | null;
  small_logo_object: string | null;
  primary_color: string;
  secondary_color: string;
  has_single_brand?: boolean;
}

/** Absent means unchanged. Clearing an image needs its explicit remove flag. */
export interface OrganizationUpdate {
  primaryColor?: string;
  secondaryColor?: string;
  logo?: File;
  smallLogo?: File;
  removeLogo?: boolean;
  removeSmallLogo?: boolean;
}

export const organizationQueryKey = ['organization'] as const;

function parseOrganization(payload: OrganizationResponse): Organization {
  return {
    organizationId: payload.organization_id,
    name: payload.name,
    logoObject: payload.logo_object,
    smallLogoObject: payload.small_logo_object,
    primaryColor: payload.primary_color,
    secondaryColor: payload.secondary_color,
    hasSingleBrand: payload.has_single_brand === true,
  };
}

/** The fields the branding document owns. The name lives on the organization. */
type Branding = Pick<
  Organization,
  'logoObject' | 'smallLogoObject' | 'primaryColor' | 'secondaryColor'
>;

function objectName(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function parseBranding(raw: Record<string, unknown>): Branding {
  return {
    logoObject: objectName(raw.logo_object),
    smallLogoObject: objectName(raw.small_logo_object),
    primaryColor: typeof raw.primary_color === 'string' ? raw.primary_color : '',
    secondaryColor: typeof raw.secondary_color === 'string' ? raw.secondary_color : '',
  };
}

/**
 * One request carries both colours and both images.
 *
 * The endpoint takes `multipart/form-data`, so a colour-only save still sends
 * FormData — which is why every field is optional and every absent one means
 * unchanged.
 */
function toFormData(update: OrganizationUpdate): FormData {
  const form = new FormData();
  if (update.primaryColor) form.append('primary_color', update.primaryColor);
  if (update.secondaryColor) form.append('secondary_color', update.secondaryColor);
  if (update.logo) form.append('logo', update.logo);
  if (update.smallLogo) form.append('small_logo', update.smallLogo);
  if (update.removeLogo) form.append('remove_logo', 'true');
  if (update.removeSmallLogo) form.append('remove_small_logo', 'true');
  return form;
}

export function useOrganization() {
  return useQuery({
    queryKey: organizationQueryKey,
    queryFn: async () =>
      parseOrganization(
        await apiClient.get<OrganizationResponse>(endpoints.organization.detail()),
      ),
  });
}

export function useUpdateOrganization() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (update: OrganizationUpdate) =>
      parseOrganization(
        await apiClient.upload<OrganizationResponse>(
          endpoints.organization.update(),
          toFormData(update),
          'put',
        ),
      ),
    // The response is what the write will make true, and the write travels by
    // Pub/Sub — so an invalidation here refetches the document before the write
    // reaches it and paints the old colours back on. The stream reports the
    // landing; this only has to report the intent.
    onSuccess: (organization) => client.setQueryData(organizationQueryKey, organization),
  });
}

export function useWatchOrganizationBranding(): void {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();

  const { data } = useFirestoreDocument<Branding>({
    path: collections.branding(organizationId ?? '_'),
    parse: parseBranding,
    enabled: Boolean(organizationId),
    endsSessionOnDenial: false,
  });

  useEffect(() => {
    if (!data) return;
    client.setQueryData<Organization>(organizationQueryKey, (current) =>
      current ? { ...current, ...data } : current,
    );
  }, [client, data]);
}

/** Paints the organization's saved colours across the app. */
export function useApplyBrandColors(primary: string | undefined, secondary: string | undefined) {
  useEffect(() => {
    setBaseBrandColors({ primary, secondary });
  }, [primary, secondary]);
}

/** Paints colours being edited, and hands the app back to the saved ones on unmount. */
export function usePreviewBrandColors(primary: string | undefined, secondary: string | undefined) {
  useEffect(() => {
    if (!primary && !secondary) return;
    setPreviewBrandColors({ primary, secondary });
    return () => setPreviewBrandColors(null);
  }, [primary, secondary]);
}
